import { TownFilterParams } from '@/types/admin-town';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@/shared/providers/theme';
import { AppAdminFormLabel, AppButton, AppUpload, SearchTextField } from '../atoms';
import { Download, Search } from '../atoms/AppIcon';

interface AdminTownFilterProps {
  onSearch?: (filters: TownFilterParams) => void;
  onCsvDownload?: () => void;
  isCsvDownloading?: boolean;
  onCsvImport?: (file: File) => void;
  isCsvImporting?: boolean;
}

const AdminTownFilter = ({
  onSearch,
  onCsvDownload,
  isCsvDownloading,
  onCsvImport,
  isCsvImporting,
}: AdminTownFilterProps) => {
  const [searchParams] = useSearchParams();
  const { colors } = useTheme();

  const [filters, setFilters] = useState<TownFilterParams>({
    name: searchParams.get('name') || '',
    postal_code: searchParams.get('postal_code') || '',
  });
  const [importFile, setImportFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  // Sync local state if searchParams change externally (e.g. browser back button)
  useEffect(() => {
    setFilters({
      name: searchParams.get('name') || '',
      postal_code: searchParams.get('postal_code') || '',
    });
  }, [searchParams]);

  const handleSearchClick = () => {
    const trimmedFilters = {
      ...filters,
      name: filters.name?.trim() || '',
      postal_code: filters.postal_code?.trim().slice(0, 255) || '',
    };
    setFilters(trimmedFilters);
    onSearch?.(trimmedFilters);
  };

  const handleFilterChange = (key: keyof TownFilterParams, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = e.currentTarget;
      handleSearchClick();
      requestAnimationFrame(() => target.focus());
    }
  };

  const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB

  const validateFile = (file: File | null): string | null => {
    if (!file) return null;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      return 'ファイル拡張子が正しくありません。';
    }
    if (file.size > MAX_FILE_SIZE) {
      return '最大アップロードの容量は30MBです。';
    }
    return null;
  };

  const handleFileChange = (file: File | null) => {
    setImportFile(file);
    setFileError(validateFile(file));
  };

  const handleImportClick = () => {
    if (!importFile) {
      setFileError('ファイルを選択してください。');
      return;
    }
    const err = validateFile(importFile);
    if (err) {
      setFileError(err);
      return;
    }
    setFileError(null);
    onCsvImport?.(importFile);
    setImportFile(null);
  };

  return (
    <div className="bg-white rounded-md border p-4 mb-4">
      <div className="grid grid-cols-4 gap-4 items-end">
        <div>
          <AppAdminFormLabel label="名称" />
          <SearchTextField
            placeholder="名称"
            value={filters.name}
            onChange={e => handleFilterChange('name', e.target.value)}
            onKeyDown={handleKeyDown}
            data-skip-enter-nav
          />
        </div>
        <div>
          <AppAdminFormLabel label="郵便番号" />
          <SearchTextField
            placeholder="郵便番号"
            value={filters.postal_code}
            onChange={e => handleFilterChange('postal_code', e.target.value)}
            onKeyDown={handleKeyDown}
            data-skip-enter-nav
          />
        </div>
        <div className="flex gap-2">
          <AppButton onClick={handleSearchClick} icon={<Search />} type="primary">
            検索
          </AppButton>
          <AppButton
            onClick={onCsvDownload}
            icon={<Download />}
            style={
              {
                backgroundColor: colors.secondary,
                ['--action-bg-hover' as string]: colors.secondaryHover,
              } as React.CSSProperties
            }
            className="hover:!bg-[var(--action-bg-hover)]"
            type="primary"
            disabled={isCsvDownloading}
            loading={isCsvDownloading}
          >
            CSV
          </AppButton>
        </div>
      </div>

      <div className="flex items-start gap-3 mt-4">
        <div className="flex-1 max-w-md">
          <AppUpload
            accept=".csv"
            onChange={handleFileChange}
            disabled={isCsvImporting}
          />
          {fileError && (
            <div className="text-red-500 text-sm mt-1">{fileError}</div>
          )}
        </div>
        <AppButton
          type="primary"
          onClick={handleImportClick}
          disabled={isCsvImporting}
          loading={isCsvImporting}
        >
          {isCsvImporting ? '処理中...' : 'CSVアップロード'}
        </AppButton>
      </div>
    </div>
  );
};

export default AdminTownFilter;
